/**
 * API Endpoints
 * Centralized endpoint definitions organized by domain
 */

export const AUTH_ENDPOINTS = {
  login: "/onboarding/admin/login",
  logout: "/onboarding/admin/logout",
  getProfile: "/onboarding/admin",
  updateProfile: "/onboarding/admin/update",
  sendOtp: "/onboarding/admin/send-otp",
  verifyOtp: "/onboarding/admin/verify-otp",
  resetPassword: "/onboarding/admin/change-password",
  changePassword: "/onboarding/admin/change-password",
  refreshToken: "/auth/refresh-token",
} as const;

export const CMS_ENDPOINTS = {
  getCms: "/cms/admin",
  updateCms: "/cms/admin",
  getContent: "/cms/content",
  updateContent: "/cms/content",
  getPages: "/cms/pages",
  getPage: "/cms/pages/:id",
  createPage: "/cms/pages",
  updatePage: "/cms/pages/:id",
  deletePage: "/cms/pages/:id",
} as const;

export const FAQ_ENDPOINTS = {
  getFaqs: "/cms/admin/faq",
  getFaq: "/cms/admin/faq/:id",
  createFaq: "/cms/admin/faq",
  updateFaq: "/cms/admin/faq/:id",
  deleteFaq: "/cms/admin/faq/:id",
} as const;

export const PRODUCT_ENDPOINTS = {
  productListing: "/products",
  productDetail: "/products",
  createProduct: "/products",
  updateProduct: "/products/:id",
  deleteProduct: "/products/:id",
} as const;

export const CATEGORY_ENDPOINTS = {
  getCategories: "/category/admin",
  getCategory: "/category/admin/:id",
  createCategory: "/category/admin",
  updateCategory: "/category/admin/:id",
  deleteCategory: "/category/admin/:id",
} as const;

export const COUPON_ENDPOINTS = {
  getCoupons: "/coupons/admin",
  getCoupon: "/coupons/admin/:id",
  createCoupon: "/coupons/admin",
  updateCoupon: "/coupons/admin/:id",
  deleteCoupon: "/coupons/admin/:id",
} as const;

export const SUBSCRIPTION_ENDPOINTS = {
  getSubscriptions: "/subscriptions/admin",
  getSubscription: "/subscriptions/admin/:id",
  updateSubscription: "/subscriptions/admin/:id",
  getUserSubscription: "/subscriptions/admin/userSubscription/:id",
  getAllTransactions: "/subscriptions/admin/transactions",
  getSettings: "/subscriptions/admin/settings",
  updateSettings: "/subscriptions/admin/settings",
  getUserPurchase: "/subscriptions/admin/userPurchase/:id",
} as const;

export const PERMISSION_ENDPOINTS = {
  getRoles: "/permissions/admin",
  getRole: "/permissions/admin/:id",
  createRole: "/permissions/admin/add",
  updateRole: "/permissions/admin/:id",
  deleteRole: "/permissions/admin/:id",
} as const;

export const SUBADMIN_ENDPOINTS = {
  getSubAdmins: "/subadmin",
  getSubAdmin: "/subadmin/:id",
  createSubAdmin: "/subadmin/add",
  updateSubAdmin: "/subadmin/:id",
  deleteSubAdmin: "/subadmin/:id",
} as const;

export const SETTINGS_ENDPOINTS = {
  getSettings: "/settings/admin",
  createSetting: "/settings/admin",
  updateSetting: "/settings/admin/:id",
  deleteSetting: "/settings/admin/:id",
} as const;

export const WORKOUT_ENDPOINTS = {
  createWorkout: "/workouts/admin",
  getWorkouts: "/workouts/admin",
  getWorkout: "/workouts/admin/:id",
  deleteWorkout: "/workouts/admin/:id",
  getMuxToken: "/workouts/user/muxtoken",

  // programs
  addProgram: "/programs/admin",
  getPrograms: "/programs/admin",
  getProgram: "/programs/admin/:id",
  updateProgram: "/programs/admin/:id",
  deleteProgram: "/programs/admin/:id",
  getWeeklyPrograms: "/programs/admin/weeklyPrograms/:id",
  updateWeeklyPrograms: "/programs/admin/weeklyPrograms/:id",
  deleteWeeklyProgram: "/programs/admin/weeklyPrograms/:id",
  refWorkouts: "/programs/admin/refWorkouts/:id",
} as const;

export const COMMUNITY_ENDPOINTS = {
  createCommunityGroup: "/community/admin",
  getCommunityGroups: "/community/admin",
  getCommunityGroup: "/community/admin/:id",
  updateCommunityGroup: "/community/admin/:id",
  deleteCommunityGroup: "/community/admin/:id",
  getCommunityComments: "/community/admin/comments",
} as const;

export const BADGE_ENDPOINTS = {
  getBadges: "/badges/admin",
  getBadge: "/badges/admin/:id",
  createBadge: "/badges/admin",
  updateBadge: "/badges/admin/:id",
  deleteBadge: "/badges/admin/:id",
} as const;

export const BRAND_ENDPOINTS = {
  getBrands: "/brands",
  getBrand: "/brands/:id",
  createBrand: "/brands",
  updateBrand: "/brands/:id",
  deleteBrand: "/brands/:id",
} as const;

export const BANNER_ENDPOINTS = {
  getBanners: "/banners",
  getBanner: "/banners/:id",
  createBanner: "/banners",
  updateBanner: "/banners/:id",
  deleteBanner: "/banners/:id",
} as const;

export const WISHLIST_ENDPOINTS = {
  getWishlist: "/wishlist",
  addToWishlist: "/wishlist",
  removeFromWishlist: "/wishlist/:id",
} as const;

export const MEDIA_ENDPOINTS = {
  upload: "/media",
  uploadMultiple: "/media/upload-multiple",
  delete: "/media/:id",
} as const;

// User Management Endpoints
export const USER_ENDPOINTS = {
  getUsers: "/users/admin",
  getUser: "/users/admin/:id",
  createUser: "/users/admin/add",
  updateUser: "/users/admin/:id",
  deleteUser: "/users/admin/:id",
  exportUsers: "/users/admin/export",
  // Legacy endpoints (keep for backward compatibility if needed)
  getUserAuditLog: "/users/:id/audit-log",
  getLoginHistory: "/users/admin/loginHistory/:id",
  getTransactionHistory: "/users/:id/transactions",
  getReferralHistory: "/users/:id/referrals",
  adjustPoints: "/users/:id/adjust-points",
  getCommunityActivity: "/users/admin/communityActivity/:id",
  getAchievements: "/users/admin/achievements/:id",
  getTransactions: "/users/admin/transactions/:id",
  getUserActivity: "/users/admin/activity/:id",
} as const;

export const PROGRESS_ENDPOINTS = {
  getPhotos: "/progressPhotos/admin/:userId",
  getWeightHistory: "/users/admin/weight/:userId",
  getWaterHistory: "/users/admin/water/:userId",
  getOverview: "/users/admin/progress/:userId",
} as const;

// Store Management Endpoints
export const STORE_ENDPOINTS = {
  getStores: "/stores",
  getStore: "/stores/:id",
  createStore: "/stores",
  updateStore: "/stores/:id",
  deleteStore: "/stores/:id",
} as const;

// Order Management Endpoints
export const ORDER_ENDPOINTS = {
  getOrders: "/orders",
  getOrder: "/orders/:id",
  updateOrderStatus: "/orders/:id/status",
  cancelOrder: "/orders/:id/cancel",
} as const;

// Notification Endpoints
export const NOTIFICATION_ENDPOINTS = {
  getNotifications: "/notifications",
  getNotification: "/notifications/:id",
  createNotification: "/notifications",
  createAdminNotification: "/notifications/admin",
  deleteAdminNotification: "/notifications/admin/:id",
  updateNotification: "/notifications/:id",
  deleteNotification: "/notifications/:id",
  markAsRead: "/notifications/:id/read",
} as const;

// Reward Management Endpoints
export const REWARD_ENDPOINTS = {
  getRewards: "/rewards",
  getReward: "/rewards/:id",
  createReward: "/rewards",
  updateReward: "/rewards/:id",
  deleteReward: "/rewards/:id",
  getRedemptionStats: "/rewards/redemption-stats",
  getAuditLog: "/rewards/audit-log",
} as const;

// Transaction Endpoints
export const TRANSACTION_ENDPOINTS = {
  getTransactions: "/transactions",
  getTransaction: "/transactions/:id",
} as const;

// Dashboard Endpoints
export const DASHBOARD_ENDPOINTS = {
  getDashboardStats: "/dashboard/admin/dashboard",
  getRevenueGraph: "/dashboard/admin/revenueGraph",
  getUserGraph: "/dashboard/admin/userGraph",
  getSubscriptionGraph: "/dashboard/admin/subscriptionGraph",
} as const;

// Promotion Endpoints
export const PROMOTION_ENDPOINTS = {
  getPromotions: "/banner/admin",
  getPromotion: "/banner/admin/:id",
  createPromotion: "/banner/admin",
  updatePromotion: "/banner/admin/:id",
  deletePromotion: "/banner/admin/:id",
} as const;

// Helper function to replace path parameters
export const replacePathParams = (
  path: string,
  params: Record<string, string | number>
): string => {
  let result = path;
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`:${key}`, String(value));
  });
  return result;
};
